import { useState, useEffect, useRef } from "react";
import { db } from "./FirebaseConfig";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import Cookies from "js-cookie";

const LS_KEY = (userId) => `chat_read_${userId}`;
const NOTIF_KEY = (userId) => `chat_notified_${userId}`;

const getReadTimes = (userId) => {
  try { return JSON.parse(localStorage.getItem(LS_KEY(userId)) || "{}"); }
  catch { return {}; }
};

const getNotifiedIds = (userId) => {
  try { return new Set(JSON.parse(localStorage.getItem(NOTIF_KEY(userId)) || "[]")); }
  catch { return new Set(); }
};

const saveNotifiedId = (userId, msgId) => {
  try {
    const ids = getNotifiedIds(userId);
    ids.add(msgId);
    localStorage.setItem(NOTIF_KEY(userId), JSON.stringify(Array.from(ids).slice(-200)));
  } catch {}
};

const getMsgTs = (data) => {
  if (data.createdAt?.seconds) return data.createdAt.seconds;
  if (data.createdAt?.toDate) return data.createdAt.toDate().getTime() / 1000;
  // serverTimestamp() can be null on first snapshot — use current time as fallback
  return Date.now() / 1000;
};

// Fetch app name from manifest.json once and cache it
let cachedAppName = null;
const getAppName = () => {
  if (cachedAppName) return Promise.resolve(cachedAppName);
  // Try multiple paths since the app may run under a basename
  const paths = [
    `${window.location.origin}/manifest.json`,
    `${window.location.href.split('#')[0].replace(/\/[^/]*$/, '')}/manifest.json`,
  ];
  const tryNext = (i) => {
    if (i >= paths.length) { cachedAppName = null; return Promise.resolve(null); }
    return fetch(paths[i])
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((m) => {
        cachedAppName = m.short_name || m.name || null;
        return cachedAppName;
      })
      .catch(() => tryNext(i + 1));
  };
  return tryNext(0);
};

const showBrowserNotification = (senderName, body, icon, appName) => {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    // Title = "AppName • Sender Name", body = message text
    const title = appName ? `${appName} • ${senderName}` : senderName;
    const n = new Notification(title, {
      body,
      icon: icon || `${window.location.origin}/logo192.png`,
      badge: `${window.location.origin}/logo192.png`,
      tag: `chat-${Date.now()}`,
    });
    setTimeout(() => n.close(), 6000);
    n.onclick = () => { window.focus(); n.close(); };
  } catch (e) {
    console.warn("[Notif] Failed to show notification:", e);
  }
};

const requestPermission = () => {
  if (!("Notification" in window)) return Promise.resolve("denied");
  if (Notification.permission === "granted") return Promise.resolve("granted");
  if (Notification.permission === "denied") return Promise.resolve("denied");
  return Notification.requestPermission();
};

const useUnreadMessages = () => {
  const [totalUnread, setTotalUnread] = useState(0);
  const currentUserId = useRef(Number(Cookies.get("userID")));
  // Use a ref set to track IDs seen in the very first snapshot load
  const initialIds = useRef(null);

  useEffect(() => {
    const uid = currentUserId.current;
    if (!uid) return;

    // Request permission upfront — non-blocking
    requestPermission();

    const messagesRef = collection(db, "messages");
    const q = query(messagesRef, where("participants", "array-contains", uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const readTimes = getReadTimes(String(uid));
      const notifiedIds = getNotifiedIds(String(uid));

      // First snapshot: just record all existing message IDs — don't notify
      if (initialIds.current === null) {
        const seen = new Set();
        snapshot.forEach((d) => seen.add(d.id));
        initialIds.current = seen;

        // Still count unread for badge
        let count = 0;
        snapshot.forEach((d) => {
          const data = d.data();
          if (data.sender_Id === uid) return;
          const peerId = String(data.sender_Id);
          const lastRead = readTimes[peerId] || 0;
          const ts = getMsgTs(data);
          if (ts > lastRead) count++;
        });
        setTotalUnread(count);
        return;
      }

      // Subsequent snapshots: process only genuinely new docs
      let count = 0;

      snapshot.docChanges().forEach((change) => {
        if (change.type !== "added") return;
        const data = change.doc.data();
        const msgId = change.doc.id;

        // Skip if we saw it on initial load
        if (initialIds.current.has(msgId)) return;
        // Track it now
        initialIds.current.add(msgId);

        // Skip own messages
        if (data.sender_Id === uid) return;

        // Skip if already notified
        if (notifiedIds.has(msgId)) return;

        const ts = getMsgTs(data);
        const peerId = String(data.sender_Id);
        const lastRead = readTimes[peerId] || 0;

        // Notify — use ts > lastRead OR if ts resolved to now (serverTimestamp fallback)
        if (ts >= lastRead) {
          const senderName = data.senderName || "Someone";
          const msgText = data.text || "Sent you a message";
          const rawLogo = data.senderLogo || null;
          const iconUrl = rawLogo
            ? (rawLogo.startsWith("http") ? rawLogo : `https://clubnet.app${rawLogo}`)
            : `${window.location.origin}/logo192.png`;
          getAppName().then((appName) => {
            console.log("[Notif] appName:", appName, "| sender:", senderName, "| msg:", msgText);
            showBrowserNotification(senderName, msgText, iconUrl, appName);
          });
          saveNotifiedId(String(uid), msgId);
        }
      });

      // Recount badge from full snapshot
      snapshot.forEach((d) => {
        const data = d.data();
        if (data.sender_Id === uid) return;
        const peerId = String(data.sender_Id);
        const lastRead = readTimes[peerId] || 0;
        const ts = getMsgTs(data);
        if (ts > lastRead) count++;
      });

      setTotalUnread(count);
    });

    return () => unsubscribe();
  }, []);

  return totalUnread;
};

export default useUnreadMessages;
